import React from "react";
import styled from "styled-components";

import { theme } from "../styles/theme";

export interface SegmentedOption<T> {
  value: T;
  label: string;
}

interface SegmentedControlProps<T> {
  options: SegmentedOption<T>[];
  value: T;
  className?: string;
  onChange: (value: T) => void;
}

/** The redesign's segmented control: a row of pills, the selected one lifted out of the track. */
export function SegmentedControl<T>({ options, value, className, onChange }: Readonly<SegmentedControlProps<T>>) {
  return (
    <Track className={className} role="tablist">
      {options.map((option) => (
        <Segment
          key={String(option.value)}
          type="button"
          role="tab"
          aria-selected={value === option.value}
          $selected={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </Segment>
      ))}
    </Track>
  );
}

const Track = styled.div`
  display: inline-flex;
  flex-direction: row;
  gap: 4px;
  padding: 4px;
  border: 1px solid ${theme.colors.gray[100]};
  border-radius: 24px;
  background-color: #ffffff;
`;

const Segment = styled.button<{ $selected: boolean }>`
  padding: 10px 24px;
  border: 0;
  border-radius: 20px;
  background-color: ${(props) => (props.$selected ? theme.colors.blue[100] : "transparent")};
  cursor: pointer;
  font-size: 15px;
  line-height: 20px;
  color: ${theme.colors.blue[800]};
  ${(props) => (props.$selected ? theme.mediumText : theme.text)};

  &:hover {
    background-color: ${(props) => (props.$selected ? theme.colors.blue[100] : theme.colors.gray[50])};
  }
`;
